
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import PaymentModal from "@/components/PaymentModal";
import { SubscriptionPlan } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { useAnalytics } from "@/hooks/useAnalytics";

interface PaymentFlowButtonProps {
  buttonText: string;
  plan: SubscriptionPlan;
  className?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  isPopular?: boolean;
  billingPeriod?: "monthly" | "quarterly" | "annual";
}

const PaymentFlowButton: React.FC<PaymentFlowButtonProps> = ({
  buttonText,
  plan,
  className,
  variant = "default",
  isPopular = false,
  billingPeriod = "monthly"
}) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getStoredAffiliateCode } = useAffiliateTracking();
  const { track } = useAnalytics();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const handleClick = () => {
    track("checkout_click", { origin: "planos_stripe", page: "/planos", plan, billingPeriod, authenticated: isAuthenticated });
    if (!isAuthenticated) {
      // Redirect to register page if not authenticated
      navigate("/pagamento");
      return;
    }
    
    // Check if user already has this plan or better
    if (user && user.plan) {
      const planOrder = { free: 0, basic: 1, premium: 2, vip: 3 };
      const currentPlanValue = planOrder[user.plan as keyof typeof planOrder] || 0;
      const selectedPlanValue = planOrder[plan as keyof typeof planOrder] || 0;
      
      // Check if subscription is active
      if (user.subscriptionEndDate) {
        const now = new Date();
        const endDate = new Date(user.subscriptionEndDate);
        
        if (currentPlanValue >= selectedPlanValue && endDate > now) {
          // User already has this plan or better and it's still active
          toast({
            title: "Você já possui este plano",
            description: "Você já tem acesso a este plano ou um plano superior.",
            variant: "default",
          });
          navigate("/gerar-jogos");
          return;
        }
      }
    }
    
    // Open payment modal if authenticated
    setIsPaymentModalOpen(true);
  };

  return (
    <>
      <Button 
        className={`w-full ${isPopular ? 'btn-gradient' : ''} ${className || ''}`} 
        variant={isPopular ? 'default' : variant}
        onClick={handleClick}
      >
        {buttonText}
      </Button>
      
      <PaymentModal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedPlan={plan}
        billingPeriod={billingPeriod}
        affiliateCode={getStoredAffiliateCode()}
      />
    </>
  );
};

export default PaymentFlowButton;
